import {useEffect, useState, useCallback, useRef} from 'react';

let activeReport;
export function readSharedView() {
  try {
    const value = JSON.parse(new URLSearchParams(window.location.search).get('view'));
    return value?.v === 1 && typeof value.id === 'string' ? value : null;
  } catch { return null; }
}
export function shouldOpenSharedSection(id) {
  return Array.isArray(readSharedView()?.open) && readSharedView().open.includes(id);
}

// Versioned presentation state. Records never go into the URL.
export function readSharedReport(id) {
  if (!id) return {};
  try {
    const value = JSON.parse(new URLSearchParams(window.location.search).get('view'));
    if (value?.v !== 1) return {};
    const settings = value.id === id ? value.settings : value.parents?.[id];
    return settings && typeof settings === 'object' ? settings : {};
  } catch { return {}; }
}

function writeSharedReport(id, headingId, settings) {
  const url = new URL(window.location.href);
  const element = document.getElementById(id);
  const open = JSON.parse(element?.dataset.shareOpen || '[]');
  const parents = {};
  for (let parent = element?.parentElement; parent; parent = parent.parentElement) {
    if (parent.dataset.shareTableState) parents[parent.id] = JSON.parse(parent.dataset.shareTableState);
  }
  url.searchParams.set('view', JSON.stringify({v: 1, id, open, parents, filters: sharedFilterSnapshot(), settings: settings}));
  url.hash = headingId || id;
  window.history.replaceState(window.history.state, '', url);
  return url.href;
}

export function useSharedReport(id, headingId, settings) {
  const [active, setActive] = useState(0);
  const encoded = JSON.stringify(settings);
  useEffect(() => {
    const refresh = () => { if (activeReport === id) setActive(value => value + 1); };
    window.addEventListener('shared-filter-change', refresh);
    return () => window.removeEventListener('shared-filter-change', refresh);
  }, [id]);
  useEffect(() => {
    if (!id || !active || activeReport !== id) return undefined;
    const timer = setTimeout(() => {
      if (activeReport !== id) return;
      writeSharedReport(id, headingId, JSON.parse(encoded));
    }, 250);
    return () => clearTimeout(timer);
  }, [id, headingId, encoded, active]);
  const activate = useCallback(() => { if (id) { activeReport = id; setActive(value => value + 1); } }, [id]);
  activate.link = () => { activate(); return writeSharedReport(id, headingId, settings); };
  return activate;
}

// Only explicitly registered display controls are included, never loaded records.
const filters = new Map();
function sharedFilterSnapshot() { return Object.fromEntries(filters); }
export function readSharedFilter(key, fallback) {
  const initial = typeof fallback === 'function' ? fallback() : fallback;
  const value = readSharedView()?.filters?.[key];
  if (value === undefined || (value !== null && !['string','number','boolean'].includes(typeof value))) return initial;
  if (initial !== null && typeof value !== typeof initial) return initial;
  return value;
}
export function useSharedFilter(key, fallback) {
  const initial = useRef(typeof fallback === 'function' ? fallback() : fallback);
  const [value, setValue] = useState(() => readSharedFilter(key, initial.current));
  const initialized = useRef(false);
  const edited = useRef(false);
  const update = useCallback(next => { edited.current = true; setValue(next); }, []);
  // Async default loading must not overwrite an explicitly shared selection.
  update.initialize = next => {
    const resolved = typeof next === 'function' ? next(value) : next;
    setValue(initialized.current ? resolved : readSharedFilter(key, resolved));
    initialized.current = true;
  };
  useEffect(() => {
    if (value !== initial.current) filters.set(key, value); else filters.delete(key);
    if (edited.current) {
      const view = readSharedView();
      if (view) {
        const url = new URL(window.location.href);
        url.searchParams.set('view', JSON.stringify({...view, filters:{...view.filters, [key]:value}}));
        window.history.replaceState(window.history.state, '', url);
      }
    }
    window.dispatchEvent(new Event('shared-filter-change'));
    return () => { filters.delete(key); };
  }, [key, value]);
  return [value, update];
}
